/**
 * @file RegistrationTab.jsx
 * @description Hardware Registration — the register/edit forms on the left,
 *              the register of every TCU on the right:
 *
 *              REGISTER — a new TCU enters the database with NO machine attached
 *              (installation maps it later). The devices table is TCU-only:
 *              model/brand default to the hardware itself (FOTAX-LITE-PRO by
 *              Autopeepal); the SIM pouch supplies the ICCID (SIM No) and the
 *              SIM's mobile number (MSISDN). On success the natural next step is
 *              offered: bench-test the fresh unit.
 *
 *              EDIT — the same fields for a TCU already in the database, for
 *              fixing what was mistyped at registration. Two things are shown
 *              but locked: the IMEI (device_data, installations and test
 *              sessions all key off it, so renaming it would strand the unit's
 *              history) and the machine it is mapped to (Installation owns that
 *              mapping and writes it with the commissioning evidence).
 *
 *              ALL DEVICES — the right-hand panel: every registered TCU in one
 *              scrollable list, searchable by IMEI / serial / machine and
 *              filterable by whether it is installed. Clicking a row loads it
 *              into the edit card, so the list is how you find a unit when you
 *              don't know its IMEI by heart.
 */
import React, { useState, useEffect, useCallback } from 'react';
import {
    Box, Card, Typography, TextField, Button, Stack, Alert, MenuItem, Grid,
    Autocomplete, Chip, CircularProgress, InputAdornment, ToggleButton, ToggleButtonGroup,
} from '@mui/material';
import {
    AddCircleOutline as RegisterIcon,
    Science as TestIcon,
    EditOutlined as EditIcon,
    Save as SaveIcon,
    Refresh as RefreshIcon,
    Search as SearchIcon,
    MemoryOutlined as TcuIcon,
} from '@mui/icons-material';
import hardwareLifecycleService from '../../../services/hardwareLifecycleService';
import { machineLabel } from '../../../utils/machineLabel';

const CATEGORIES = ['MEWP', 'Battery Pack', 'Tower Light'];

const BLANK = {
    device_id: '', serial_number: '',
    model_number: 'FOTAX-LITE-PRO', brand: 'Autopeepal',
    product_category: 'MEWP', sim_no: '', mobile_number: '',
};

// The fields the edit card may write — mirrors the server's whitelist.
const EDITABLE = ['serial_number', 'sim_no', 'mobile_number', 'model_number', 'brand', 'product_category'];

// A device row → the editable subset, with nulls flattened to '' so the inputs
// stay controlled and the dirty check compares like with like.
const editableOf = (d) => EDITABLE.reduce((o, k) => ({ ...o, [k]: d?.[k] ?? '' }), {});

/** One TCU in the right-hand register. Click → it opens in the edit card. */
const DeviceRow = ({ d, selected, onPick }) => (
    <Box
        onClick={() => onPick(d)}
        sx={{
            px: 1.25, py: 1, borderRadius: '10px', cursor: 'pointer',
            border: '1px solid', borderColor: selected ? 'primary.main' : 'divider',
            bgcolor: selected ? 'primary.50' : 'background.paper',
            display: 'flex', alignItems: 'center', gap: 1,
            transition: 'background-color .12s, border-color .12s',
            '&:hover': { borderColor: 'primary.light', bgcolor: 'action.hover' },
        }}
    >
        <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography variant="body2" fontWeight={700} sx={{ fontFamily: 'monospace' }} noWrap>
                {d.device_id}
            </Typography>
            <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>
                {[d.serial_number, d.sim_no ? `SIM ${d.sim_no}` : null].filter(Boolean).join(' · ') || 'No serial yet'}
            </Typography>
        </Box>
        <Chip
            size="small"
            variant={d.equipment_id ? 'filled' : 'outlined'}
            color={d.equipment_id ? 'primary' : 'default'}
            label={d.equipment_id ? machineLabel(d) : 'Spare'}
            sx={{ height: 22, fontWeight: 700, maxWidth: 160 }}
        />
    </Box>
);

const RegistrationTab = ({ onRegistered, onStartTest }) => {
    // ── Register a new TCU ───────────────────────────────────────────────────
    const [form, setForm] = useState(BLANK);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');
    const [done, setDone] = useState(null);

    const set = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));

    // ── Edit a registered TCU ────────────────────────────────────────────────
    const [devices, setDevices] = useState([]);
    const [loadingDevices, setLoadingDevices] = useState(false);
    const [picked, setPicked] = useState(null);   // the device as stored
    const [draft, setDraft] = useState(null);     // the editable copy
    const [saving, setSaving] = useState(false);
    const [editError, setEditError] = useState('');
    const [editDone, setEditDone] = useState('');

    // ── The register of every TCU (right-hand panel) ─────────────────────────
    const [query, setQuery] = useState('');
    const [fitment, setFitment] = useState('all');   // all | installed | spare

    const loadDevices = useCallback(async () => {
        setLoadingDevices(true);
        try {
            const res = await hardwareLifecycleService.pickers();
            setDevices(Array.isArray(res?.devices) ? res.devices : []);
        } catch (e) {
            setEditError(e.message || 'Could not load the registered devices');
        } finally {
            setLoadingDevices(false);
        }
    }, []);

    useEffect(() => { loadDevices(); }, [loadDevices]);

    const pick = (device) => {
        setPicked(device);
        setDraft(device ? editableOf(device) : null);
        setEditError('');
        setEditDone('');
    };

    const setDraftField = (k) => (e) => setDraft(d => ({ ...d, [k]: e.target.value }));

    // Only what actually changed is sent — an untouched field is left alone
    // server-side, so two people editing different fields cannot clobber each other.
    const changed = draft && picked
        ? EDITABLE.filter(k => String(draft[k] ?? '') !== String(picked[k] ?? ''))
        : [];

    // What the right-hand register shows: the search matches the three handles
    // an installer actually knows a unit by (IMEI, machine serial, machine id).
    const visible = devices.filter(d => {
        if (fitment === 'installed' && !d.equipment_id) return false;
        if (fitment === 'spare' && d.equipment_id) return false;
        const q = query.trim().toLowerCase();
        if (!q) return true;
        return [d.device_id, d.serial_number, d.equipment_id, d.sim_no, d.mobile_number]
            .some(v => String(v || '').toLowerCase().includes(q));
    });
    const installedCount = devices.filter(d => d.equipment_id).length;

    const submit = async () => {
        const deviceId = form.device_id.trim();
        if (!deviceId) { setError('Device ID (IMEI) is required'); return; }
        setBusy(true); setError('');
        try {
            await hardwareLifecycleService.register({ ...form, device_id: deviceId });
            setDone(deviceId);
            setForm(BLANK);
            loadDevices();   // the new unit is immediately editable next door
            if (onRegistered) onRegistered(deviceId);
        } catch (e) {
            setError(/409/.test(e.message) ? `Device ${deviceId} is already registered.` : (e.message || 'Registration failed'));
        } finally {
            setBusy(false);
        }
    };

    const saveEdit = async () => {
        if (!picked || changed.length === 0) return;
        setSaving(true); setEditError(''); setEditDone('');
        try {
            const payload = changed.reduce((o, k) => ({ ...o, [k]: draft[k] }), {});
            await hardwareLifecycleService.updateDevice(picked.device_id, payload);
            setEditDone(`${picked.device_id} updated — ${changed.length} field${changed.length > 1 ? 's' : ''} saved.`);
            // Re-read so the card shows what is actually STORED: the server
            // substitutes the defaults when model / make / category are cleared.
            const res = await hardwareLifecycleService.pickers();
            const list = Array.isArray(res?.devices) ? res.devices : [];
            setDevices(list);
            const fresh = list.find(d => d.device_id === picked.device_id) || null;
            setPicked(fresh);
            setDraft(fresh ? editableOf(fresh) : null);
        } catch (e) {
            setEditError(/404/.test(e.message) ? `${picked.device_id} is no longer registered.` : (e.message || 'Update failed'));
        } finally {
            setSaving(false);
        }
    };

    return (
        <Grid container spacing={2.5} alignItems="flex-start">

            {/* ── The forms: register, then edit ─────────────────────────────── */}
            <Grid size={{ xs: 12, lg: 7 }}>
              <Stack spacing={2.5}>
                <Card variant="outlined" sx={{ borderRadius: '14px', p: 2.5 }}>
                    <Typography variant="subtitle1" fontWeight={800} sx={{ mb: 0.25 }}>Register a new TCU</Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
                        Only the database entry — no machine is attached here. Mapping to a machine happens in
                        Installation, after the unit has been tested.
                    </Typography>

                    {error && <Alert severity="error" sx={{ mb: 1.5 }}>{error}</Alert>}
                    {done && (
                        <Alert severity="success" sx={{ mb: 1.5 }}
                            action={
                                <Button size="small" color="inherit" startIcon={<TestIcon />}
                                    onClick={() => onStartTest && onStartTest(done)}>
                                    Bench test it
                                </Button>
                            }>
                            Device {done} registered.
                        </Alert>
                    )}

                    <Stack spacing={1.75}>
                        <TextField label="Device ID (IMEI)" value={form.device_id} onChange={set('device_id')}
                            required fullWidth size="small" placeholder="8644100829XXXXX"
                            helperText="The equipment serial number is stamped automatically when the TCU is mapped to a machine at installation."
                            inputProps={{ style: { fontFamily: 'monospace' } }} />
                        <Stack direction="row" spacing={1.5}>
                            <TextField label="SIM No (ICCID, on the pouch)" value={form.sim_no} onChange={set('sim_no')}
                                fullWidth size="small" placeholder="8991942603400459XXXX"
                                inputProps={{ style: { fontFamily: 'monospace' } }} />
                            <TextField label="Mobile number (MSISDN)" value={form.mobile_number} onChange={set('mobile_number')}
                                fullWidth size="small" placeholder="57541829327XX"
                                inputProps={{ style: { fontFamily: 'monospace' } }} />
                        </Stack>
                        <Stack direction="row" spacing={1.5}>
                            <TextField label="Hardware model" value={form.model_number} onChange={set('model_number')}
                                fullWidth size="small" helperText="The TCU model, not the machine's" />
                            <TextField label="Hardware make" value={form.brand} onChange={set('brand')}
                                fullWidth size="small" />
                        </Stack>
                        <TextField select label="Fits equipment category" value={form.product_category}
                            onChange={set('product_category')} fullWidth size="small">
                            {CATEGORIES.map(c => <MenuItem key={c} value={c}>{c}</MenuItem>)}
                        </TextField>
                        <Button variant="contained" onClick={submit} disabled={busy}
                            startIcon={<RegisterIcon />} sx={{ alignSelf: 'flex-start', fontWeight: 700, textTransform: 'none' }}>
                            {busy ? 'Registering…' : 'Register device'}
                        </Button>
                    </Stack>
                </Card>

                {/* ── Edit a registered TCU ──────────────────────────────────── */}
                <Card variant="outlined" sx={{ borderRadius: '14px', p: 2.5 }}>
                    <Typography variant="subtitle1" fontWeight={800} sx={{ mb: 0.25 }}>Edit a registered TCU</Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
                        Fix what was mistyped at registration. The IMEI cannot be changed — it is what the
                        unit's telemetry and test sessions are filed under.
                    </Typography>

                    {editError && <Alert severity="error" sx={{ mb: 1.5 }}>{editError}</Alert>}
                    {editDone && <Alert severity="success" sx={{ mb: 1.5 }}>{editDone}</Alert>}

                    <Autocomplete
                        options={devices}
                        value={picked}
                        onChange={(_, v) => pick(v)}
                        loading={loadingDevices}
                        isOptionEqualToValue={(o, v) => o.device_id === v.device_id}
                        getOptionLabel={(o) => o.device_id || ''}
                        filterOptions={(opts, { inputValue }) => {
                            // Installers know a unit by its serial as often as its IMEI.
                            const q = inputValue.trim().toLowerCase();
                            if (!q) return opts;
                            return opts.filter(o =>
                                String(o.device_id || '').toLowerCase().includes(q) ||
                                String(o.serial_number || '').toLowerCase().includes(q) ||
                                String(o.equipment_id || '').toLowerCase().includes(q));
                        }}
                        renderOption={(props, o) => (
                            <Box component="li" {...props} key={o.device_id}>
                                <Box sx={{ minWidth: 0 }}>
                                    <Typography variant="body2" fontWeight={700} sx={{ fontFamily: 'monospace' }}>
                                        {o.device_id}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                        {[o.serial_number, o.equipment_id ? `on ${o.equipment_id}` : 'not installed']
                                            .filter(Boolean).join(' · ')}
                                    </Typography>
                                </Box>
                            </Box>
                        )}
                        renderInput={(params) => (
                            <TextField {...params} size="small" label="Device to edit"
                                placeholder="Search by IMEI, serial or machine" />
                        )}
                    />

                    {!picked ? (
                        <Box sx={{ py: 5, textAlign: 'center' }}>
                            <EditIcon sx={{ fontSize: 40, color: 'text.disabled', mb: 1 }} />
                            <Typography variant="body2" color="text.secondary" fontWeight={600}>
                                Pick a device to edit its details
                            </Typography>
                            <Typography variant="caption" color="text.disabled">
                                {loadingDevices ? 'Loading…' : `${devices.length} registered`}
                            </Typography>
                        </Box>
                    ) : (
                        <Stack spacing={1.75} sx={{ mt: 2 }}>
                            {/* Locked identity: what this unit IS, and where it is fitted. */}
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                                <Chip size="small" label={`IMEI ${picked.device_id}`}
                                    sx={{ fontWeight: 800, fontFamily: 'monospace', height: 24 }} />
                                <Chip size="small" variant="outlined"
                                    label={picked.equipment_id ? `Installed on ${machineLabel(picked)}` : 'Not installed'}
                                    color={picked.equipment_id ? 'primary' : 'default'}
                                    sx={{ fontWeight: 700, height: 24 }} />
                            </Box>

                            <TextField label="Equipment serial number" value={draft.serial_number}
                                onChange={setDraftField('serial_number')} fullWidth size="small"
                                placeholder="BTRC025D00XXX"
                                helperText="The mapped MACHINE's serial — stamped automatically at installation; edit only to correct it." />
                            <Stack direction="row" spacing={1.5}>
                                <TextField label="SIM No (ICCID, on the pouch)" value={draft.sim_no}
                                    onChange={setDraftField('sim_no')} fullWidth size="small"
                                    inputProps={{ style: { fontFamily: 'monospace' } }} />
                                <TextField label="Mobile number (MSISDN)" value={draft.mobile_number}
                                    onChange={setDraftField('mobile_number')} fullWidth size="small"
                                    inputProps={{ style: { fontFamily: 'monospace' } }} />
                            </Stack>
                            <Stack direction="row" spacing={1.5}>
                                <TextField label="Hardware model" value={draft.model_number}
                                    onChange={setDraftField('model_number')} fullWidth size="small"
                                    helperText="The TCU model, not the machine's" />
                                <TextField label="Hardware make" value={draft.brand}
                                    onChange={setDraftField('brand')} fullWidth size="small" />
                            </Stack>
                            <TextField select label="Fits equipment category" value={draft.product_category || 'MEWP'}
                                onChange={setDraftField('product_category')} fullWidth size="small">
                                {CATEGORIES.map(c => <MenuItem key={c} value={c}>{c}</MenuItem>)}
                            </TextField>

                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                                <Button variant="contained" onClick={saveEdit}
                                    disabled={saving || changed.length === 0}
                                    startIcon={<SaveIcon />} sx={{ fontWeight: 700, textTransform: 'none' }}>
                                    {saving ? 'Saving…' : 'Save changes'}
                                </Button>
                                <Button onClick={() => pick(picked)} disabled={saving || changed.length === 0}
                                    sx={{ fontWeight: 700, textTransform: 'none' }}>
                                    Undo
                                </Button>
                                <Typography variant="caption" color="text.secondary">
                                    {changed.length === 0 ? 'No changes yet' : `${changed.length} field${changed.length > 1 ? 's' : ''} changed`}
                                </Typography>
                            </Box>
                        </Stack>
                    )}
                </Card>
              </Stack>
            </Grid>

            {/* ── Every registered TCU ───────────────────────────────────────── */}
            <Grid size={{ xs: 12, lg: 5 }}>
                <Card variant="outlined" sx={{ borderRadius: '14px', p: 2.5, position: { lg: 'sticky' }, top: 16 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.25 }}>
                        <Typography variant="subtitle1" fontWeight={800}>All registered devices</Typography>
                        <Chip size="small" label={devices.length} sx={{ height: 20, fontWeight: 800 }} />
                        <Button size="small" onClick={loadDevices} disabled={loadingDevices}
                            startIcon={loadingDevices ? <CircularProgress size={13} /> : <RefreshIcon sx={{ fontSize: 16 }} />}
                            sx={{ ml: 'auto', textTransform: 'none', fontWeight: 700 }}>
                            Refresh
                        </Button>
                    </Box>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
                        {installedCount} installed on a machine · {devices.length - installedCount} spare.
                        Click a unit to edit it.
                    </Typography>

                    <Stack direction="row" spacing={1} sx={{ mb: 1.5 }} alignItems="center">
                        <TextField
                            value={query} onChange={(e) => setQuery(e.target.value)}
                            size="small" fullWidth placeholder="Search IMEI, serial, machine or SIM"
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon sx={{ fontSize: 18, color: 'text.disabled' }} />
                                    </InputAdornment>
                                ),
                            }}
                        />
                        <ToggleButtonGroup exclusive size="small" value={fitment}
                            onChange={(_, v) => v && setFitment(v)}>
                            <ToggleButton value="all" sx={{ textTransform: 'none', fontWeight: 700, px: 1.25 }}>All</ToggleButton>
                            <ToggleButton value="installed" sx={{ textTransform: 'none', fontWeight: 700, px: 1.25 }}>Installed</ToggleButton>
                            <ToggleButton value="spare" sx={{ textTransform: 'none', fontWeight: 700, px: 1.25 }}>Spare</ToggleButton>
                        </ToggleButtonGroup>
                    </Stack>

                    {loadingDevices && devices.length === 0 ? (
                        <Box sx={{ py: 5, textAlign: 'center' }}>
                            <CircularProgress size={22} />
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                                Loading devices…
                            </Typography>
                        </Box>
                    ) : visible.length === 0 ? (
                        <Box sx={{ py: 5, textAlign: 'center' }}>
                            <TcuIcon sx={{ fontSize: 40, color: 'text.disabled', mb: 1 }} />
                            <Typography variant="body2" color="text.secondary" fontWeight={600}>
                                {devices.length === 0 ? 'No TCU registered yet' : 'No device matches that search'}
                            </Typography>
                        </Box>
                    ) : (
                        <>
                            <Stack spacing={0.75} sx={{ maxHeight: '62vh', overflowY: 'auto', pr: 0.5 }}>
                                {visible.map(d => (
                                    <DeviceRow key={d.device_id} d={d}
                                        selected={picked?.device_id === d.device_id}
                                        onPick={pick} />
                                ))}
                            </Stack>
                            <Typography variant="caption" color="text.disabled" sx={{ display: 'block', mt: 1 }}>
                                Showing {visible.length} of {devices.length}
                            </Typography>
                        </>
                    )}
                </Card>
            </Grid>
        </Grid>
    );
};

export default RegistrationTab;
